import { type ReactNode, useState } from "react";

import MemberPermissionsTag from "common/components/MemberPermissionsTag";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";
import ProfileImage from "common/components/ui/ProfileImage";
import { Tag } from "common/components/ui/Tag";
import { formatDate } from "common/helpers/utils";
import type { Member } from "common/types";

import MemberActions from "../MemberActions";
import MemberActionsDropdown from "../MemberActionsDropdown";
import MemberStatusTag from "../MemberStatusTag";

interface MobileDrawerContentProps {
  member: Member;
}

function DetailItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex px-4 justify-between border-b border-b-black-200 py-2 items-center">
      <span className="flex text-button-sm">{label}</span>
      {children}
    </div>
  );
}

export default function MobileMemberDetails({
  member,
}: MobileDrawerContentProps) {
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [action, setAction] = useState<"remove" | "revoke" | "permissions">();

  const {
    first_name: firstName,
    last_name: lastName,
    role,
    permissions,
    customer_id: memberId,
    email,
    created_at: createdAt,
    status,
  } = member;

  return (
    <>
      <div className="flex pl-16 pr-4 py-3 border-b border-b-black-200 gap-x-2 lg:gap-x-2.5 items-start">
        <div className="flex gap-x-3 items-start flex-1">
          {status === "pending" ? (
            <ProfileImage size="md" src="" />
          ) : (
            <AimfoxAvatar firstName={firstName} lastName={lastName} size="md" />
          )}

          <div className="flex flex-col gap-y-1">
            <span className="text-sm-bold">
              {memberId
                ? `${firstName} ${lastName}`
                : `New ${role === "member" ? "Member" : "Admin"}`}
            </span>
            <p className="text-black-500 text-xs-medium">{email}</p>
          </div>
        </div>
        <MemberActionsDropdown
          member={member}
          setAction={(selectedAction) => {
            setAction(selectedAction);
            setIsActionOpen(true);
          }}
        />
      </div>

      <DetailItem label="Role">
        <MemberPermissionsTag permissions={permissions} role={role} />
      </DetailItem>
      <DetailItem label="Added">
        <Tag variant="tertiary">{formatDate(createdAt)}</Tag>
      </DetailItem>
      <DetailItem label="Status">
        <MemberStatusTag memberStatus={status} memberRole={role} size="md" />
      </DetailItem>

      <Drawer open={isActionOpen} onOpenChange={setIsActionOpen}>
        <DrawerContent>
          <MemberActions
            action={action}
            member={member}
            onClose={() => setIsActionOpen(false)}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}
